// `needs_reconsent` is the only way to know, and it must be READ rather than inferred from an
// error. IT IS NOT AN ERROR, and must not read as one.
import { useEffect } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { useJdConnect } from "../hooks/useJdConnect";
import { reportSwallowed } from "../lib/reportSwallowed";

/* * Should the prompt show at all? Connected AND flagged — never for a user who isn't connected. */
export function needsReconsent(
  status: { connected?: boolean; needs_reconsent?: boolean } | null | undefined,
): boolean {
  return Boolean(status?.connected && status.needs_reconsent);
}

export const RECONSENT_COPY = {
  title: "John Deere needs re-approving",
  // Says what is true (nothing is broken), what changed (we ask for more), and what it costs (one click).
  body:
    "Your John Deere connection is still working, but it was approved before we started asking for everything " +
    "we now need. Re-approve it once and everything keeps working — nothing is lost, and you won’t be asked again.",
  action: "Re-approve John Deere",
} as const;

/*
 *
 * The inline prompt. Renders NOTHING unless the connection is live and flagged, so it can be dropped
 * anywhere JD work begins without a caller having to gate it.
 */
export function JdReconsentNote({ className = "" }: { className?: string }) {
  const { status, refresh, connect, connecting } = useJdConnect();
  // `useJdConnect` does NOT fetch on mount — without this the status stays null and this component
  // renders nothing forever, which is exactly the silent-no-op failure it exists to prevent.
  useEffect(() => {
    void refresh().catch((err) =>
      reportSwallowed("refresh John Deere status", err),
    );
  }, [refresh]);
  if (!needsReconsent(status)) return null;
  return (
    <div
      className={`rounded border border-amber-500/40 bg-amber-500/5 p-2 text-[11px] ${className}`}
    >
      <span className="block font-medium text-fg">{RECONSENT_COPY.title}</span>
      <span className="mt-0.5 block leading-snug text-faint">
        {RECONSENT_COPY.body}
      </span>
      {/*
 the SAME `connect` the first-time flow uses — re-consent is just consent again, and a second
          code path would be a second thing to keep working
*/}
      <button
        onClick={connect}
        disabled={connecting}
        className="mt-1.5 flex items-center gap-1 rounded bg-accent px-2 py-1 text-[10px] font-medium text-canvas hover:bg-accent-hover disabled:opacity-50"
      >
        {connecting ? (
          <Loader2 size={10} className="animate-spin" />
        ) : (
          <RefreshCw size={10} />
        )}{" "}
        {RECONSENT_COPY.action}
      </button>
    </div>
  );
}
